import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { translateApiMessage } from "../utils/formErrors";
import { toastApiError } from "../utils/toastErrors";
import { useDispatch, useSelector } from "react-redux";
import DatePicker from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import Select from "../components/ui/Select";
import { useNavigate } from "react-router-dom";
import { createOrder } from "../actions/performaActions";
import { RESET_ADD_PERFORMA } from "../actions/actionTypes";

const orderOptions = [
  { value: "در انتظار", label: "در انتظار" },
  { value: "ثبت سفارش", label: "ثبت سفارش" },
  { value: "رد شده", label: "رد شده" },
];

const errorTranslationMap = {
  "performa with this prf order no already exists.":
    "پرفورما با این شماره ثبت سفارش از قبل وجود دارد.",
  "performa with this prfVCodeInt already exists.":
    "پرفورما با این شماره پرونده از قبل وجود دارد.",
  "performa with this prf number already exists.":
    "پرفورما با این شماره پیش فاکتور از قبل وجود دارد.",
};

const ORDER_LABELS = {
  prf_order_no: "شماره ثبت سفارش",
  prfVCodeInt: "شماره پرونده",
  prf_number: "شماره پیش فاکتور",
  prf_date: "تاریخ پیش فاکتور",
  prf_expire_date: "تاریخ انقضا",
  prf_freight_price: "کرایه حمل",
  FOB: "FOB",
  prf_total_price: "ارزش کل",
  prf_seller_country: "کشور فروشنده",
  prf_status: "وضعیت",
  currency_allocation_status: "وضعیت تخصیص ارز",
  payment_instrument: "ابزار پرداخت",
  bank_info: "اطلاعات بانک",
  rial_deposit: "ریال واریزی",
};

const emptyForm = {
  prf_order_no: "",
  prf_number: "",
  prf_date: "",
  prf_expire_date: "",
  prf_freight_price: "",
  FOB: "",
  prf_total_price: "",
  prf_seller_country: "",
  prf_status: "",
  currency_allocation_status: "",
  prfVCodeInt: "",
  payment_instrument: "",
  bank_info: "",
  rial_deposit: "",
};

const translateErrors = (errorObj) => {
  const translatedErrors = {};
  Object.keys(errorObj || {}).forEach((key) => {
    if (Array.isArray(errorObj[key])) {
      translatedErrors[key] = errorObj[key].map(
        (msg) => errorTranslationMap[msg] || translateApiMessage(msg)
      );
      return;
    }
    translatedErrors[key] = errorTranslationMap[errorObj[key]] || translateApiMessage(errorObj[key]);
  });
  return translatedErrors;
};

const selectStyles = {
  control: (provided) => ({
    ...provided,
    fontFamily: "Vazirmatn, sans-serif",
    direction: "rtl",
    borderColor: "#D1D5DB",
    borderRadius: "0.375rem",
    padding: "0.35rem 0.5rem",
    boxShadow: "none",
    backgroundColor: "#FFFFFF",
    minHeight: "44px",
    "&:hover": {
      borderColor: "#2563EB",
    },
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#9CA3AF",
    textAlign: "right",
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#374151",
    textAlign: "right",
  }),
  menu: (provided) => ({
    ...provided,
    fontFamily: "Vazirmatn, sans-serif",
    direction: "rtl",
    borderRadius: "0.375rem",
    boxShadow:
      "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    backgroundColor: "#FFFFFF",
    zIndex: 30,
  }),
  option: (provided, state) => ({
    ...provided,
    textAlign: "right",
    backgroundColor: state.isSelected
      ? "#2563EB"
      : state.isFocused
        ? "#DBEAFE"
        : "#FFFFFF",
    color: state.isSelected ? "#FFFFFF" : "#374151",
  }),
};

const AddOrder = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { orderCreation } = useSelector((state) => state.order);

  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(createOrder({
      ...formData,
      rial_deposit: formData.rial_deposit === "" ? null : formData.rial_deposit,
    }));
  };

  useEffect(() => {
    if (orderCreation.success) {
      setErrors({});
      setFormData(emptyForm);
      toast.success("ثبت سفارش با موفقیت ایجاد شد.");
      navigate("/reged-orders");
      dispatch({ type: RESET_ADD_PERFORMA });
      return;
    }

    if (orderCreation.error) {
      if (typeof orderCreation.error === "object" && orderCreation.error !== null) {
        setErrors(translateErrors(orderCreation.error));
        toastApiError({ response: { data: orderCreation.error } }, ORDER_LABELS, "ایجاد ثبت سفارش ناموفق بود.");
      } else {
        toast.error(translateApiMessage(orderCreation.error) || "ایجاد ثبت سفارش ناموفق بود.");
      }
      dispatch({ type: RESET_ADD_PERFORMA });
    }
  }, [dispatch, navigate, orderCreation.error, orderCreation.success]);

  useEffect(() => {
    const freightPrice = parseFloat(formData.prf_freight_price) || 0;
    const fobPrice = parseFloat(formData.FOB) || 0;
    setFormData((prev) => ({
      ...prev,
      prf_total_price: (freightPrice + fobPrice).toFixed(2),
    }));
  }, [formData.prf_freight_price, formData.FOB]);

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-10 sm:mt-16 md:mt-20 lg:mt-24 xl:mt-32 max-w-2xl mx-auto p-6 bg-white rounded-lg shadow-md font-vazirmatn direction-rtl"
    >
      <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
        ثبت سفارش جدید
      </h2>

      <div className="mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-1 text-right">
          تاریخ ثبت:
        </span>
        <div className="relative">
          <DatePicker
            value={formData.prf_date}
            onChange={(date) =>
              setFormData((prev) => ({
                ...prev,
                prf_date: date ? date.format("YYYY-MM-DD") : "",
              }))
            }
            calendar={persian}
            locale={persian_fa}
            format="YYYY-MM-DD"
            placeholder="تاریخ را انتخاب کنید"
            required
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right bg-white shadow-sm hover:border-primary transition-colors duration-200 font-vazirmatn"
            calendarPosition="bottom-right"
            inputClass="w-full"
            containerStyle={{ direction: "rtl" }}
          />
        </div>
        {errors.prf_date && (
          <div className="text-red-600 text-sm mt-1 text-right">
            {errors.prf_date[0]}
          </div>
        )}
      </div>

      <div className="mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-1 text-right">
          تاریخ اعتبار:
        </span>
        <div className="relative">
          <DatePicker
            value={formData.prf_expire_date}
            onChange={(date) =>
              setFormData((prev) => ({
                ...prev,
                prf_expire_date: date ? date.format("YYYY-MM-DD") : "",
              }))
            }
            calendar={persian}
            locale={persian_fa}
            format="YYYY-MM-DD"
            placeholder="تاریخ اعتبار را انتخاب کنید"
            required
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right bg-white shadow-sm hover:border-primary transition-colors duration-200 font-vazirmatn"
            calendarPosition="bottom-right"
            inputClass="w-full"
            containerStyle={{ direction: "rtl" }}
          />
        </div>
        {errors.prf_expire_date && (
          <div className="text-red-600 text-sm mt-1 text-right">
            {errors.prf_expire_date[0]}
          </div>
        )}
      </div>

      {[
        {
          label: "شماره ثبت سفارش:",
          name: "prf_order_no",
          type: "number",
          required: true,
          min: 0,
        },
        {
          label: "شماره پرونده:",
          name: "prfVCodeInt",
          type: "number",
          required: true,
          min: 0,
        },
        {
          label: "شماره پیش فاکتور:",
          name: "prf_number",
          type: "text",
          required: true,
        },
      ].map((field) => (
        <div className="mb-4" key={field.name}>
          <span className="block text-sm font-medium text-gray-700 mb-1 text-right">
            {field.label}
          </span>
          <input
            type={field.type}
            name={field.name}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={
              errors[field.name] ? errors[field.name][0] : field.label.replace(":", "")
            }
            className={`w-full px-3 py-2 border ${
              errors[field.name] ? "border-red-600" : "border-gray-300"
            } rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right`}
            required={field.required}
            min={field.min}
          />
          {errors[field.name] && (
            <div className="text-red-600 text-sm mt-1 text-right">
              {errors[field.name][0]}
            </div>
          )}
        </div>
      ))}

      {[
        { label: "FOB:", name: "FOB", required: true },
        { label: "کرایه حمل:", name: "prf_freight_price", required: true },
        { label: "ارزش کل:", name: "prf_total_price", required: true },
        { label: "ریال واریزی:", name: "rial_deposit" },
      ].map((field) => (
        <div className="mb-4" key={field.name}>
          <span className="block text-sm font-medium text-gray-700 mb-1 text-right">
            {field.label}
          </span>
          <input
            type="number"
            name={field.name}
            value={formData[field.name]}
            onChange={handleChange}
            placeholder={field.label.replace(":", "")}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right"
            min={0}
            required={field.required}
          />
        </div>
      ))}

      <div className="mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-1 text-right">
          کشور فروشنده:
        </span>
        <input
          type="text"
          name="prf_seller_country"
          value={formData.prf_seller_country}
          onChange={handleChange}
          placeholder="کشور فروشنده"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right"
          required
        />
      </div>

      <div className="mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-1 text-right">
          اطلاعات بانک:
        </span>
        <input
          type="text"
          name="bank_info"
          value={formData.bank_info}
          onChange={handleChange}
          placeholder="اطلاعات بانک"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right"
        />
      </div>

      <div className="mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-1 text-right">
          ابزار پرداخت:
        </span>
        <input
          type="text"
          name="payment_instrument"
          value={formData.payment_instrument}
          onChange={handleChange}
          placeholder="ابزار پرداخت"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right"
        />
      </div>

      <div className="mb-4">
        <label
          htmlFor="currency_allocation_status"
          className="block text-sm font-medium text-gray-700 mb-1 text-right"
        >
          وضعیت تخصیص ارز:
        </label>
        <input
          id="currency_allocation_status"
          name="currency_allocation_status"
          type="text"
          list="currency-allocation-status-options"
          value={formData.currency_allocation_status}
          onChange={handleChange}
          placeholder="وضعیت تخصیص ارز"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary text-right"
        />
        <datalist id="currency-allocation-status-options">
          <option value="آماده برای تخصیص" />
          <option value="تخصیص یافته" />
          <option value="رد شده" />
        </datalist>
        {errors.currency_allocation_status && (
          <div className="text-red-600 text-sm mt-1 text-right">
            {errors.currency_allocation_status[0]}
          </div>
        )}
      </div>

      <div className="mb-4">
        <span className="block text-sm font-medium text-gray-700 mb-1 text-right">
          وضعیت:
        </span>
        <Select
          name="prf_status"
          value={orderOptions.find((option) => option.value === formData.prf_status)}
          onChange={(selectedOption) => {
            setFormData((prev) => ({
              ...prev,
              prf_status: selectedOption ? selectedOption.value : "",
            }));
            setErrors((prev) => ({ ...prev, prf_status: "" }));
          }}
          options={orderOptions}
          placeholder="وضعیت"
          isClearable
          styles={selectStyles}
        />
        {errors.prf_status && (
          <div className="text-red-600 text-sm mt-1 text-right">
            {errors.prf_status[0]}
          </div>
        )}
      </div>

      <button
        type="submit"
        className="w-full mx-auto px-4 py-2 bg-primary text-white rounded-md hover:bg-blue-700 active:bg-blue-800 transition-colors duration-300 disabled:bg-gray-400 disabled:cursor-not-allowed"
        disabled={orderCreation.loading}
      >
        {orderCreation.loading ? "در حال ایجاد..." : "ایجاد ثبت سفارش"}
      </button>
    </form>
  );
};

export default AddOrder;
